import React, { useState, useMemo } from 'react';
import { Fuel, X, Search, ChevronDown } from 'lucide-react';

/**
 * Componente de filtro de combustível com dropdown pesquisável
 * Substitui a lista de botões por uma interface mais limpa e escalável
 */
export default function FuelFilter({ 
  combustiveis = [], 
  selecionados = [], 
  onChange,
  placeholder = "Filtrar por combustível",
  multiple = false,
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar combustíveis pela busca
  const filteredCombustiveis = useMemo(() => {
    if (!searchTerm.trim()) return combustiveis;
    return combustiveis.filter(c => 
      c.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [combustiveis, searchTerm]);

  // Obter nomes dos selecionados para exibição
  const selectedNames = useMemo(() => {
    if (selecionados.length === 0) return null;
    return selecionados
      .map(id => combustiveis.find(c => c.id === id)?.type)
      .filter(Boolean)
      .join(', ');
  }, [selecionados, combustiveis]);

  // Handler para seleção
  const handleSelect = (combustivelId) => {
    if (multiple) {
      // Toggle para múltipla seleção
      const newSelection = selecionados.includes(combustivelId)
        ? selecionados.filter(id => id !== combustivelId)
        : [...selecionados, combustivelId];
      onChange(newSelection);
    } else {
      // Seleção única
      const newSelection = selecionados.includes(combustivelId) ? [] : [combustivelId];
      onChange(newSelection);
      setIsOpen(false);
    }
  };

  // Limpar filtro
  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} data-testid="fuel-filter">
      {/* Trigger do Dropdown */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 w-full px-3 py-2 
          border rounded-lg text-sm bg-white
          hover:border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          transition-all cursor-pointer
          ${selecionados.length > 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}
        `}
        data-testid="fuel-filter-trigger"
      >
        <Fuel size={16} className={selecionados.length > 0 ? 'text-orange-500' : 'text-gray-400'} />
        <span className={`flex-1 text-left truncate ${selecionados.length > 0 ? 'text-orange-700 font-medium' : 'text-gray-500'}`}>
          {selectedNames || placeholder}
        </span>
        {selecionados.length > 0 ? (
          <button
            onClick={handleClear}
            className="p-0.5 hover:bg-orange-200 rounded"
            data-testid="fuel-filter-clear"
          >
            <X size={14} className="text-orange-500" />
          </button>
        ) : (
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <>
          {/* Overlay para fechar ao clicar fora */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div 
            className="absolute z-50 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            data-testid="fuel-filter-menu"
          >
            {/* Campo de busca */}
            <div className="p-2 border-b">
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                  data-testid="fuel-filter-search"
                />
              </div>
            </div>

            {/* Lista de opções */}
            <div className="max-h-48 overflow-y-auto">
              {filteredCombustiveis.length === 0 ? (
                <p className="p-3 text-sm text-gray-400 text-center">
                  {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum combustível disponível'}
                </p>
              ) : (
                filteredCombustiveis.map(comb => {
                  const isSelected = selecionados.includes(comb.id);
                  return (
                    <button
                      key={comb.id}
                      onClick={() => handleSelect(comb.id)}
                      className={`
                        w-full px-3 py-2 text-left text-sm flex items-center gap-2
                        hover:bg-gray-50 transition-colors
                        ${isSelected ? 'bg-orange-50 text-orange-700' : 'text-gray-700'}
                      `}
                      data-testid={`fuel-option-${comb.id}`}
                    >
                      <div className={`
                        w-4 h-4 rounded border flex items-center justify-center
                        ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}
                      `}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span>{comb.type}</span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer com ações */}
            {multiple && selecionados.length > 0 && (
              <div className="p-2 border-t bg-gray-50 flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {selecionados.length} selecionado(s)
                </span>
                <button
                  onClick={handleClear}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                >
                  Limpar
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
